import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material';
import {HttpErrorResponse} from '@angular/common/http';
import {select, Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';

import {Group, IGroupedDevice} from '../../shared';
import {NegativeConfirmComponent} from '../../shared/components/negative-confirm/negative-confirm.component';
import {AuthService, GroupService, UserService} from '../../core';
import {GroupNewDialogComponent} from './group-new-dialog/group-new-dialog.component';
import {DeviceAddDialogComponent} from './device-add-dialog/device-add-dialog.component';
import {MemberMgmtDialogComponent} from './member-mgmt-dialog/member-mgmt-dialog.component';
import * as fromRoot from '../../reducers';
import * as group from '../../core/store/actions/group.action';
import * as user from '../../core/store/actions/user.action';

import {forkJoin, Observable, Subject} from 'rxjs';


import {GetOrganizationDevices} from '../../afb-api/actions/device.actions';
import {OrganizationWithToken} from '../../core/models/core';
import {tap} from 'rxjs/operators';
import {Device} from '../../afb-api/models/device';
import {OrganizationUser} from '../../afb-api/models/organization-user';
import {takeUntil} from 'rxjs/internal/operators';

@Component({
  selector: 'awair-group',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.scss']
})

export class GroupComponent implements OnInit, OnDestroy {

  initialized: boolean = false;

  groupedDeviceList: IGroupedDevice[];
  selectedGroup: IGroupedDevice;
  selectedGroupId: string = null;
  unassignedGroup: IGroupedDevice;
  // Organization user
  orgUserList: OrganizationUser[] = [];
  activeOrgMemberList: OrganizationUser[] = [];
  pendingOrgMemberList: OrganizationUser[] = [];
  orgAdminList: OrganizationUser[] = [];
  orgOwner: OrganizationUser;

  groupMgmtDialog: MatDialogRef<MemberMgmtDialogComponent>;

  selectedOrganization$: Observable<OrganizationWithToken> = this.store.pipe(
    select(fromRoot.selectedOrganization),
    tap((selectedOrganization: OrganizationWithToken) => this.store.dispatch(new user.RequestOrgUserListAction(selectedOrganization.token)))
  );

  private groupedDeviceList$: Observable<IGroupedDevice[]>;
  private orgUserList$: Observable<OrganizationUser[]>;
  private activeOrgMemberList$: Observable<OrganizationUser[]>;
  private pendingOrgMemberList$: Observable<OrganizationUser[]>;
  private orgAdminList$: Observable<OrganizationUser[]>;
  private orgOwner$: Observable<OrganizationUser>;
  private isGroupAccessSetting$: Observable<boolean>;
  private destroyed$ = new Subject<boolean>();

  private cachedGroup: Group;

  constructor(private store: Store<fromRoot.State>,
              private actions$: Actions,
              private authService: AuthService,
              private groupService: GroupService,
              private userService: UserService,
              private dialog: MatDialog,
              private myDialogRef: MatDialogRef<GroupComponent>) {
  }

  ngOnInit() {
    // Group Mgmt Dialog Response
    this.actions$.pipe(
      ofType(user.SET_GROUP_USER_ACCESS_SUCCESS),
      takeUntil(this.destroyed$),
      tap(() => {
        this.groupMgmtDialog.componentInstance.isLoading = false;
        this.groupMgmtDialog.close();
      })
    ).subscribe();
    this.actions$.pipe(
      ofType(user.SET_GROUP_USER_ACCESS_FAIL),
      takeUntil(this.destroyed$),
      tap(() => {
        this.groupMgmtDialog.componentInstance.isLoading = false;
        alert('Failed to apply changes.\nPlease try again later.');
      })
    ).subscribe();

    // Request organization users to always include members/admins who accepted the request
    // this.store.dispatch(new user.RequestOrgUserListAction(this.authService.getOrgToken()));
    this.groupedDeviceList$ = this.groupService.getGroupedDeviceList();
    this.groupedDeviceList$.subscribe((groupedDeviceList: IGroupedDevice[]) => {
      this.groupedDeviceList = groupedDeviceList;

      if (!this.initialized) {
        const copyGroupedDeviceList = [...this.groupedDeviceList];
        const topMostGroup = copyGroupedDeviceList.sort((a: IGroupedDevice, b: IGroupedDevice) => {
          if (a.name < b.name) {
            return -1;
          } else if (a.name > b.name) {
            return 1;
          } else {
            return 0;
          }
        })[0];
        // Select the top most group by default
        this.selectedGroupId = topMostGroup.id;
        this.selectedGroup = topMostGroup;
        this.cachedGroup = this.selectedGroup;
        this.initialized = true;

      } else {
        if (this.cachedGroup.id == null) {
          // If device list refreshed due to create group, get index using name
          const index = this.groupedDeviceList.map((groupedDevice: IGroupedDevice) => groupedDevice.name).indexOf(this.cachedGroup.name);
          if (index !== -1) {
            this.selectedGroup = this.groupedDeviceList[index];
            this.cachedGroup = this.selectedGroup;
            this.selectedGroupId = this.groupedDeviceList[index].id;
          }

        } else {
          // else, get by id
          this.selectedGroup = this.getGroupedDeviceById(this.groupedDeviceList, this.cachedGroup.id);
          this.cachedGroup = this.selectedGroup;
          this.selectedGroupId = this.selectedGroup.id;
        }
      }

      // Set unassigned group
      this.unassignedGroup = this.getGroupedDeviceById(this.groupedDeviceList, null);
    });

    /**
     * Init organization users
     */
    this.orgUserList$ = this.userService.getOrgUserList();
    this.orgUserList$.subscribe((orgUserList: OrganizationUser[]) => {
      this.orgUserList = orgUserList;
    });
    this.activeOrgMemberList$ = this.userService.getActiveOrgMemberList();
    this.activeOrgMemberList$.subscribe((activeOrgMemberList: OrganizationUser[]) => {
      this.activeOrgMemberList = activeOrgMemberList;
    });
    this.pendingOrgMemberList$ = this.userService.getPendingOrgMemberList();
    this.pendingOrgMemberList$.subscribe((pendingOrgMemberList: OrganizationUser[]) => {
      this.pendingOrgMemberList = pendingOrgMemberList;
    });
    this.orgAdminList$ = this.userService.getActiveOrgAdminList();
    this.orgAdminList$.subscribe((orgAdminList: OrganizationUser[]) => {
      this.orgAdminList = orgAdminList;
    });
    this.orgOwner$ = this.userService.getOrgOwner();
    this.orgOwner$.subscribe((orgOwner: OrganizationUser) => {
      this.orgOwner = orgOwner;
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  createGroup(organizationToken: string): void {
    const groupNewDialogRef = this.dialog.open(GroupNewDialogComponent, {disableClose: true});
    groupNewDialogRef.afterClosed().subscribe((groupName: string) => {
      // Request group list again for now
      // Just add group next time
      this.store.dispatch(new group.RequestGroupListAction(organizationToken));

      // Select created group
      this.cachedGroup = {id: null, name: groupName};
    });
  }

  selectGroupId(groupId: string): void {
    this.selectedGroupId = groupId;
    this.selectedGroup = this.getGroupedDeviceById(this.groupedDeviceList, this.selectedGroupId);
    this.cachedGroup = this.selectedGroup;
  }

  // Group Detail Outputs
  assignToGroup(data: { groupId: string, deviceList: Device[] }, organizationToken: string): void {
    const obs = [];
    for (let i = 0; i < data.deviceList.length; i++) {
      obs.push(this.groupService.assignGroup(
        organizationToken,
        data.groupId,
        data.deviceList[i].deviceType,
        data.deviceList[i].deviceId)
      );
    }

    forkJoin(obs).subscribe(
      (response: any) => {
        // this.store.dispatch(new device.RequestDeviceListAction(organizationToken));
        this.store.dispatch(new GetOrganizationDevices({organizationToken}));
        // Group access may change if certain device of a user is added for the first time
        this.store.dispatch(new user.RequestOrgUserListAction(organizationToken));
      },
      (error: HttpErrorResponse) => {
        // this.store.dispatch(new device.RequestDeviceListAction(organizationToken));
        this.store.dispatch(new GetOrganizationDevices({organizationToken}));
        alert('Failed to move device(s).\nPlease try again later.');
      }
    );
  }

  addDevice(selectedGroup: IGroupedDevice, organizationToken: string): void {
    const groupNewDialogRef = this.dialog.open(DeviceAddDialogComponent, {disableClose: true});
    groupNewDialogRef.componentInstance.unassignedGroup = this.unassignedGroup;
    groupNewDialogRef.componentInstance.selectedGroupName = selectedGroup.name;
    groupNewDialogRef.afterClosed().subscribe((data: any) => {
      if (data != null) {
        const selectedDeviceList: Device[] = [...data];
        this.assignToGroup({'groupId': selectedGroup.id, 'deviceList': selectedDeviceList}, organizationToken);
      }
    });
  }

  removeFromGroup(data: { group: IGroupedDevice, deviceList: Device[] }, organizationToken: string): void {
    const obs = [];

    for (let i = 0; i < data.deviceList.length; i++) {
      obs.push(this.groupService.removeDeviceFromGroup(
        organizationToken,
        data.group.id,
        data.deviceList[i].deviceType,
        data.deviceList[i].deviceId)
      );
    }

    forkJoin(obs).subscribe(
      (response: any) => {
        // this.store.dispatch(new device.RequestDeviceListAction(organizationToken));
        this.store.dispatch(new GetOrganizationDevices({organizationToken}));
      },
      (error: HttpErrorResponse) => {
        alert('Failed to remove device(s).\nPlease try again later.');
      }
    );
  }

  deleteGroup(selectedGroup: IGroupedDevice, organizationToken: string): void {
    const dialogRef = this.dialog.open(NegativeConfirmComponent, {disableClose: true});
    dialogRef.componentInstance.header = 'Are you sure?';
    dialogRef.componentInstance.subHeader = `This will delete ${selectedGroup.name} from your groups.`;
    dialogRef.componentInstance.btnText = 'Yes, Delete';
    dialogRef.componentInstance.confirm.subscribe(() => {
      this.groupService.deleteGroup(organizationToken, selectedGroup.id).subscribe(
        (newResponse: { message: string }) => {
          this.store.dispatch(new group.RequestGroupListAction(organizationToken));
          // this.store.dispatch(new device.RequestDeviceListAction(organizationToken));
          this.store.dispatch(new GetOrganizationDevices({organizationToken}));
          this.cachedGroup = {name: null, id: null};

          dialogRef.close();
        },
        (error: HttpErrorResponse) => {
          alert('Failed to delete group.\nPlease try again later.');
          dialogRef.close();
        }
      );
    });
  }

  manageMember(selectedGroup: IGroupedDevice, organizationToken: string): void {
    this.groupMgmtDialog = this.dialog.open(MemberMgmtDialogComponent, {disableClose: true});
    this.groupMgmtDialog.componentInstance.selectedGroup = selectedGroup;
    this.groupMgmtDialog.componentInstance.orgOwner = this.orgOwner;
    this.groupMgmtDialog.componentInstance.orgUserList = this.orgUserList;
    this.groupMgmtDialog.componentInstance.orgAdminList = this.orgAdminList;
    this.groupMgmtDialog.componentInstance.activeOrgMemberList = this.activeOrgMemberList;
    this.groupMgmtDialog.componentInstance.pendingOrgMemberList = this.pendingOrgMemberList;
    this.groupMgmtDialog.componentInstance.save.subscribe((groupAccessData: {
      removeMemberList: OrganizationUser[],
      inviteMemberList: string[],
      addMemberList: OrganizationUser[],
      groupId: string
    }) => {
      this.groupMgmtDialog.componentInstance.isLoading = true;
      this.store.dispatch(new user.SetGroupUserAccessAction({
        orgToken: organizationToken,
        inviteMemberList: groupAccessData.inviteMemberList,
        removeMemberList: groupAccessData.removeMemberList,
        addMemberList: groupAccessData.addMemberList,
        groupId: groupAccessData.groupId
      }));
    });
  }

  done(): void {
    this.myDialogRef.close();
  }

  private getGroupedDeviceById(groupedDeviceList: IGroupedDevice[], groupId: string): IGroupedDevice {
    const index = groupedDeviceList.map((groupedDevice: IGroupedDevice) => groupedDevice.id).indexOf(groupId);

    return index !== -1 ? groupedDeviceList[index] : groupedDeviceList[0];
  }
}
